import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ArrowRight, Send } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import ConfirmTransferDialog from "./ConfirmTransferDialog";

const fmt = (n) => Number(n || 0).toLocaleString("en-ZA", { maximumFractionDigits: 2 });
const newRef = () => (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`).toString();

function StorePick({ label, value, onChange, stores, testid }) {
  return (
    <div className="space-y-1 min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder="Select store" /></SelectTrigger>
        <SelectContent>{stores.map((s) => <SelectItem key={s.store_id} value={s.store_id} data-testid={`${testid}-${s.store_id}`}>{s.store_name}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

export default function TransferForm({ onDone }) {
  const { stores, active } = useStore();
  const [src, setSrc] = useState(active?.store_id || "");
  const [dst, setDst] = useState("");
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [lines, setLines] = useState({});
  const [notes, setNotes] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(newRef());

  useEffect(() => { if (!dst && stores.length > 1) setDst(stores.find((s) => s.store_id !== src)?.store_id || ""); }, [stores, src]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setLines({}); setProducts([]);
    if (src && dst && src !== dst) api.get("/transfers/products", { params: { source_store_id: src, dest_store_id: dst } })
      .then((r) => setProducts(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Could not load products"));
  }, [src, dst]);

  const name = (id) => stores.find((s) => s.store_id === id)?.store_name || "";
  const byId = Object.fromEntries(products.map((p) => [p.product_id, p]));
  const rows = Object.entries(lines).map(([id, q]) => {
    const p = byId[id]; const n = Number(q);
    const err = q === "" ? "Enter a quantity" : !(n > 0) ? "Must be more than 0" : !p.decimal && !Number.isInteger(n) ? `Whole ${p.unit || "units"} only`
      : n > p.source_qty ? `Only ${fmt(p.source_qty)} available` : null;
    return { ...p, q: n, raw: q, err };
  });
  const shown = products.filter((p) => !(p.product_id in lines) && search &&
    `${p.name} ${p.sku}`.toLowerCase().includes(search.toLowerCase())).slice(0, 12);
  const valid = rows.length && rows.every((r) => !r.err) && src !== dst;

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/transfers", { source_store_id: src, dest_store_id: dst, notes, client_ref: ref.current,
        lines: rows.map((x) => ({ product_id: x.product_id, quantity: x.q })) });
      toast.success(`Transfer ${r.data.reference} completed`);
      ref.current = newRef(); setLines({}); setNotes(""); setConfirm(false); setSearch("");
      onDone?.(r.data);
      const p = await api.get("/transfers/products", { params: { source_store_id: src, dest_store_id: dst } }); setProducts(p.data);
    } catch (e) { if (e.response) ref.current = newRef(); toast.error(e.response?.data?.detail || "Network problem — press Confirm again; the transfer will not be applied twice"); } finally { setBusy(false); }
  };

  return (
    <Card className="p-4 space-y-4" data-testid="transfer-form">
      <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
        <StorePick label="From (source)" value={src} onChange={setSrc} stores={stores} testid="transfer-source" />
        <ArrowRight className="w-4 h-4 mb-3 text-muted-foreground" />
        <StorePick label="To (destination)" value={dst} onChange={setDst} stores={stores} testid="transfer-dest" />
      </div>
      {src && src === dst && <div className="text-sm text-[#C05746]" data-testid="transfer-same-store-error">Source and destination must be different stores.</div>}
      <div className="relative">
        <Input placeholder={`Search ${name(src)} products by name or SKU`} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="transfer-search" />
        {shown.length > 0 && (
          <div className="absolute z-20 mt-1 w-full rounded-md border bg-background shadow-lg max-h-72 overflow-auto" data-testid="transfer-search-results">
            {shown.map((p) => (
              <div key={p.product_id} className="flex items-center justify-between gap-2 p-2 border-b text-sm" data-testid={`transfer-result-${p.sku}`}>
                <div className="min-w-0"><div className="truncate">{p.name}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">{p.sku} · {fmt(p.source_qty)} {p.unit} in {name(src)}</div>
                  {!p.dest_carried && <div className="text-[11px] text-[#C05746]" data-testid={`transfer-not-carried-${p.sku}`}>{name(dst)} does not carry this product — management must first add/map it there.</div>}</div>
                <Button size="sm" variant="outline" disabled={!p.dest_carried || p.source_qty <= 0} onClick={() => { setLines((l) => ({ ...l, [p.product_id]: "" })); setSearch(""); }} data-testid={`transfer-add-${p.sku}`}><Plus className="w-3 h-3" /></Button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="space-y-2" data-testid="transfer-lines">
        {!rows.length && <div className="text-sm text-muted-foreground">Search and add one or more products.</div>}
        {rows.map((r) => (
          <div key={r.product_id} className="rounded-md border p-3 grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_8rem_auto] gap-2 items-center" data-testid={`transfer-line-${r.sku}`}>
            <div className="min-w-0 text-sm col-span-2 sm:col-span-1"><div className="truncate">{r.name}</div>
              <div className="font-mono text-[11px] text-muted-foreground">{r.sku} · available {fmt(r.source_qty)} {r.unit} · {name(dst)} has {fmt(r.dest_qty)}</div>
              {r.err ? <div className="text-[11px] text-[#C05746]" data-testid={`transfer-line-error-${r.sku}`}>{r.err}</div>
                : <div className="text-[11px] text-muted-foreground" data-testid={`transfer-line-impact-${r.sku}`}>{name(src)} {fmt(r.source_qty)} → {fmt(r.source_qty - r.q)} · {name(dst)} {fmt(r.dest_qty)} → {fmt(r.dest_qty + r.q)}</div>}</div>
            <Input type="number" inputMode="decimal" min="0" step={r.decimal ? "0.01" : "1"} value={r.raw} placeholder={r.unit}
              onChange={(e) => setLines((l) => ({ ...l, [r.product_id]: e.target.value }))} className="text-right font-mono" data-testid={`transfer-qty-${r.sku}`} />
            <Button size="icon" variant="ghost" onClick={() => setLines(({ [r.product_id]: _, ...rest }) => rest)} data-testid={`transfer-remove-${r.sku}`}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </div>
      <Textarea placeholder="Reason / notes" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="transfer-notes" />
      <div className="flex justify-end">
        <Button className="rounded-full" disabled={!valid || busy} onClick={() => setConfirm(true)} data-testid="transfer-review-btn"><Send className="w-4 h-4 mr-1" />Review transfer ({rows.length})</Button>
      </div>
      <ConfirmTransferDialog open={confirm} onOpenChange={setConfirm} rows={rows} from={name(src)} to={name(dst)} notes={notes} busy={busy} onConfirm={submit} />
    </Card>
  );
}
